/*
 * File: c:\Users\j.mazat\Desktop\Autobuy\AutobuyBackOffice\src\Services\ServiceGeneral.ts
 * Project: c:\Users\j.mazat\Desktop\Autobuy\AutobuyBackOffice
 * Created Date: Wednesday June 7th 2023
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Wednesday June 7th 2023 11:33:19 am
 * Modified By: the developer formerly known as Jonathan Mazat at <you@you.you>
 * -----
 * Copyright (c) 2023 Autobuy SAS
 * -----
 */

import AuthenticationError from "Error/AuthenticationError";
import UserSession from "Utils/UserSession";
import Axios, { AxiosResponse } from "axios";

class AuthError extends Error {
  public status: number;

  constructor(message: string, status: number) {
    super(`${status}: ${message}`);
    this.status = status;
  }
}

/**
 * Helps manage and centralize common behavior of services
 */
class ServiceGeneral {
  static END = "end";
  static CONTINUE = "continue";

  /**
   * Manage the response of axios (Redirect to login if 401, redirect to page error if 500)
   * @param {Object} data The data returned by axios
   * @param {*} history The history object of react router
   * @returns continue if we should continue operations, end if the compoenent calling this function should stop
   */
  static async handleAxiosResponse(data: AxiosResponse<any, any>) {
    const status = data.status;
    switch (status) {
      case 500:
        return ServiceGeneral.END;
      case 401:
        throw new AuthenticationError("Identification expired.");
      default:
        break;
    }
    return ServiceGeneral.CONTINUE;
  }

  static async manageAxiosResponse(func: Function) {
    try {
      return await func();
    } catch (e: any) {
      if (e.response) {
        const status = e.response.status;
        switch (status) {
          case 500:
            return ServiceGeneral.END;
          case 502:
          case 401:
            const ret = await UserSession.tokenExpired();
            if (!ret) {
              throw new AuthError("Unable to reset token", 401);
            }
            return func();
          default:
            break;
        }
      } else {
        throw e;
      }
    }
  }

  static async manageCall(url: string, method: "post" | "get" | "put", body?: {}) {
    const headers = await UserSession.buildAxiosHeader();
    switch (method) {
      case "post":
        return Axios.post(url, body, headers);
      case "get":
        return Axios.get(url, headers);
      case "put":
        return Axios.put(url, body, headers);
    }
  }

  /**
   * Check if the server is reached, if yes returns the response otherwise returns a server error like response
   * @param {*} error The axios error
   */
  static interpretError(error: any) {
    if (error.response === undefined) {
      return {
        status: 500,
        data: {},
      };
    } else if (error.response.status === 401) {
      throw new AuthenticationError("Identification expired.");
    }

    return error.response;
  }
}

export default ServiceGeneral;
